import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Loader2 } from "lucide-react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { toast } from "sonner";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { calendarDay, isReschedulableDay } from "@/lib/appointment-reschedule";
import { rescheduleAppointment } from "@/services/appointment-reschedule.service";

interface RescheduleAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentJoin;
  onSuccess?: (updatedAppointment: AppointmentJoin) => void;
}

export default function RescheduleAppointmentModal({
  isOpen,
  onClose,
  appointment,
  onSuccess,
}: RescheduleAppointmentModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) setSelectedDate(undefined);
  }, [isOpen]);

  const currentMonth = appointment.date ? new Date(`${appointment.date}T00:00:00`) : undefined;

  const handleSubmit = async () => {
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    setLoading(true);
    try {
      const updatedAppointment = await rescheduleAppointment(appointment.id, calendarDay(selectedDate));
      toast.success("Appointment rescheduled successfully");
      if (onSuccess) {
        onSuccess(updatedAppointment);
      }
      onClose();
    } catch (error) {
      console.error("Failed to reschedule appointment:", error);
      toast.error(fieldActionMessage(error, "Failed to reschedule appointment"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Reschedule Appointment</DialogTitle>
          <DialogDescription>
            Select a new date for this appointment. It will return to pending and assignment will restart.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate ?? currentMonth}
            onSelect={setSelectedDate}
            disabled={(date) => !isReschedulableDay(date, appointment.date)}
            className="rounded-md border"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!selectedDate || loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Rescheduling...
              </>
            ) : (
              "Reschedule Appointment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
